import { Icon } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';
import type { GridDataFormat } from './formats';
import type { CellRule } from './types';

interface HeaderAlignmentControlsProps {
  current: GridDataFormat;
  defaultRule: CellRule;
  disabled: boolean;
  onChange: (next: GridDataFormat, changedKeys?: (keyof GridDataFormat)[]) => void;
}

/** Column-specific heading controls; the organization rule remains the default. */
export function HeaderAlignmentControls({ current, defaultRule, disabled, onChange }: HeaderAlignmentControlsProps) {
  const { t } = useI18n();
  const mode = current.headerMode ?? 'default';
  const align = current.headerAlign ?? 'auto';
  const wrapMode = current.headerWrapMode ?? defaultRule.wrapMode ?? (defaultRule.wrap ? 'wrap' : 'clip');
  const buttonClass = (selected: boolean) => `flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 ${selected ? 'bg-brand/10 text-brand font-bold' : 'text-text hover:bg-surface-hover'}`;

  return (
    <div className="flex min-w-0 max-w-full flex-wrap items-center gap-1">
      <span className="shrink-0 text-sm text-text-muted sm:text-xs">{t('grid.headersLabel')}</span>
      <select
        aria-label={t('grid.headerAlignLabel')}
        title={t('grid.headerAlignLabel')}
        value={mode}
        disabled={disabled}
        onChange={(event) => {
          const nextMode = event.target.value;
          if (nextMode !== 'default' && nextMode !== 'same' && nextMode !== 'custom') return;
          onChange({
            ...current,
            headerMode: nextMode,
            ...(nextMode === 'custom' ? { headerAlign: align, headerWrapMode: wrapMode } : {}),
          }, ['headerMode', 'headerAlign', 'headerWrapMode']);
        }}
        className="h-7 min-w-0 cursor-pointer rounded bg-transparent px-1 text-sm text-fg outline-none hover:bg-surface-hover focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 sm:text-xs"
      >
        <option value="default">{t('grid.headerAlignDefault')}</option>
        <option value="same">{t('grid.headerAlignSame')}</option>
        <option value="custom">{t('grid.headerAlignCustom')}</option>
      </select>
      {mode === 'custom' && (
        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-1" role="group" aria-label={t('grid.headerAlignLabel')}>
          <div className="flex shrink-0 items-center rounded p-[1px]" role="group" aria-label={t('grid.alignLabel')}>
            {([
              { key: 'auto', icon: 'alignAuto', label: t('grid.alignAuto') },
              { key: 'left', icon: 'alignLeft', label: t('grid.alignLeft') },
              { key: 'center', icon: 'alignCenter', label: t('grid.alignCenter') },
              { key: 'right', icon: 'alignRight', label: t('grid.alignRight') },
            ] as const).map(({ key, icon, label }) => (
              <button key={key} type="button" disabled={disabled} title={label} aria-label={label} aria-pressed={align === key}
                onClick={() => onChange({ ...current, headerAlign: key }, ['headerAlign'])} className={buttonClass(align === key)}>
                <Icon name={icon} className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
          <div className="flex shrink-0 items-center rounded p-[1px]" role="group" aria-label={t('grid.wrapLabel')}>
            {([
              { key: 'wrap', icon: 'wrapText', label: t('grid.wrapText') },
              { key: 'clip', icon: 'clipText', label: t('grid.clipText') },
              { key: 'shrink', icon: 'shrinkText', label: t('grid.shrinkText') },
              { key: 'expand', icon: 'expandText', label: t('grid.expandText') },
              { key: 'overflow', icon: 'overflowText', label: t('grid.overflowText') },
            ] as const).map(({ key, icon, label }) => (
              <button key={key} type="button" disabled={disabled} title={label} aria-label={label} aria-pressed={wrapMode === key}
                onClick={() => onChange({ ...current, headerWrapMode: key }, ['headerWrapMode'])} className={buttonClass(wrapMode === key)}>
                <Icon name={icon} className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
